import { Injectable, OnDestroy } from '@angular/core';
import { FormArray } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AiToolForm } from '../forms';

/**
 * Ensures that only one form is created for each tool and manages parent array notifications
 */
@Injectable()
export class AiToolFormRegistryService implements OnDestroy {
  // Map of tool ID to form instance
  private readonly toolFormRegistry = new Map<string, AiToolForm>();

  // Track which FormArrays contain each tool (for multi-parent change notification)
  private readonly parentArrays = new Map<string, Set<FormArray>>();

  // Store subscriptions so we can unsubscribe on cleanup
  private readonly changeSubscriptions = new Map<string, Subscription>();

  /**
   * Gets an existing tool form or registers a new one if not found
   * Returns the same instance so multiple capabilities share the tool
   */
  getOrRegister(toolId: string, formFactory: () => AiToolForm): AiToolForm {
    if (!toolId) {
      // For new tools without IDs, always create a new form
      const f = formFactory();
      return f;
    }

    // Check if we already have a form for this tool
    const existingForm = this.toolFormRegistry.get(toolId);
    if (existingForm) {
      // Return the same instance so all capabilities share it
      return existingForm;
    }

    // Create and register a new form
    const newForm = formFactory();
    this.toolFormRegistry.set(toolId, newForm);

    // Set up change listener once for this tool
    this.setupChangeListener(toolId, newForm);

    return newForm;
  }

  /**
   * Register that a FormArray contains this tool
   * When the tool changes, all registered parent arrays will be notified
   */
  registerParent(toolId: string, parentArray: FormArray): void {
    if (!toolId) return;

    if (!this.parentArrays.has(toolId)) {
      this.parentArrays.set(toolId, new Set());
    }
    this.parentArrays.get(toolId)!.add(parentArray);
  }

  /**
   * Unregister a parent array when tool is removed
   */
  unregisterParent(toolId: string, parentArray: FormArray): void {
    if (!toolId) return;

    const parents = this.parentArrays.get(toolId);
    if (parents) {
      parents.delete(parentArray);
      if (parents.size === 0) {
        this.parentArrays.delete(toolId);
      }
    }
  }

  /**
   * Set up a change listener for a tool form
   * When the tool changes, notify all parent arrays
   */
  private setupChangeListener(toolId: string, toolForm: AiToolForm): void {
    if (this.changeSubscriptions.has(toolId)) {
      return; // Already set up
    }

    // When the tool form changes, notify all parent arrays
    const subscription = toolForm.valueChanges.subscribe(() => {
      this.notifyAllParents(toolId);
    });

    // Store subscription so we can unsubscribe later
    this.changeSubscriptions.set(toolId, subscription);
  }

  /**
   * Trigger updateValueAndValidity on all parent arrays
   * This causes each parent's valueChanges to emit, propagating up to capability forms
   */
  private notifyAllParents(toolId: string): void {
    const parents = this.parentArrays.get(toolId);
    if (!parents || parents.size === 0) return;

    // Trigger each parent's valueChanges by updating validity
    parents.forEach((parent) => {
      // This will cause the parent's valueChanges to emit
      // which propagates up to the capability form
      parent.updateValueAndValidity({
        emitEvent: true, // Emit valueChanges
        onlySelf: false, // Propagate to parents
      });
    });
  }

  /**
   * Updates the registered form for a tool ID
   */
  updateRegistry(toolId: string, form: AiToolForm): void {
    if (!toolId) return;
    this.toolFormRegistry.set(toolId, form);
  }

  /**
   * Checks if a tool form is registered
   */
  hasRegisteredForm(toolId: string): boolean {
    return this.toolFormRegistry.has(toolId);
  }

  /**
   * Clears registry entries that are no longer needed
   * @param activeToolIds Set of tool IDs that are still active
   */
  cleanupRegistry(activeToolIds: Set<string>): void {
    // Remove any forms for tools that are no longer used
    for (const toolId of this.toolFormRegistry.keys()) {
      if (!activeToolIds.has(toolId)) {
        // Unsubscribe from valueChanges to prevent memory leaks
        const subscription = this.changeSubscriptions.get(toolId);
        if (subscription) {
          subscription.unsubscribe();
          this.changeSubscriptions.delete(toolId);
        }

        this.toolFormRegistry.delete(toolId);
        this.parentArrays.delete(toolId);
      }
    }
  }

  /**
   * Clean up all subscriptions when service is destroyed
   */
  ngOnDestroy(): void {
    // Unsubscribe from all valueChanges subscriptions
    this.changeSubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.changeSubscriptions.clear();
    this.toolFormRegistry.clear();
    this.parentArrays.clear();
  }
}
